import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Input } from '@picsio/ui';
import {
  CheckIcon, CloseIcon, Delete, Edit,
} from '@picsio/icons';

import { deleteProductVendor } from '../../../store/productVendors/delete';
import localization from '../../../shared/strings';
import Toast from '../../Toast/index';
import Tooltip from '../../Tooltip/Tooltip';
import { productFieldInputProps } from '../constants';
import styles from './styles.module.scss';

const l10n = localization.PRODUCT_INFO.vendor;

const VendorRow = ({
  item,
  isEditing,
  editValue,
  hasEditError,
  onStartRename,
  onChangeEditValue,
  onCommitRename,
  onCancelRename,
}) => {
  const dispatch = useDispatch();
  const isProcessing = useSelector(
    (state) => state.productVendors.processingIds.includes(item._id),
  );
  const handleStartRename = useCallback(() => {
    onStartRename(item);
  }, [item, onStartRename]);

  const handleEditFocus = useCallback((e) => {
    e.target.select();
  }, []);

  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') onCommitRename();
    if (e.key === 'Escape') onCancelRename();
  }, [onCommitRename, onCancelRename]);

  const handleDelete = useCallback(async () => {
    const result = await dispatch(deleteProductVendor({ productVendorId: item._id, title: item.title }));
    /** rejection here means a genuine failure; a user-cancelled confirm dialog also
     *  resolves as fulfilled with `{ cancelled: true }` and needs no feedback */
    if (deleteProductVendor.rejected.match(result)) {
      Toast(l10n.deleteFailed, { autoClose: false, type: 'error' });
    }
  }, [dispatch, item._id, item.title]);

  return (
    <li className={styles.row}>
      <Choose>
        <When condition={isEditing}>
          <div className={styles.editForm}>
            <Input
              value={editValue}
              error={hasEditError}
              autoFocus
              inputProps={productFieldInputProps}
              onChange={onChangeEditValue}
              onFocus={handleEditFocus}
              onKeyDown={handleEditKeyDown}
            />
            <Tooltip content={l10n.cancel} placement="top">
              <IconButton size="md" className="btnCollection" color="inherit" disabled={isProcessing} onClick={onCancelRename}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
            <Tooltip content={l10n.confirm} placement="top">
              <IconButton size="md" className="btnCollection" color="inherit" disabled={isProcessing} onClick={onCommitRename}>
                <CheckIcon />
              </IconButton>
            </Tooltip>
          </div>
        </When>
        <Otherwise>
          <span className={styles.title}>{item.title}</span>
          <div className={styles.actions}>
            <Tooltip content={l10n.renameTooltip} placement="top">
              <IconButton size="md" className="btnCollection" color="inherit" disabled={isProcessing} onClick={handleStartRename}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip content={l10n.deleteTooltip} placement="top">
              <IconButton size="md" className="btnCollection" color="inherit" disabled={isProcessing} onClick={handleDelete}>
                <Delete />
              </IconButton>
            </Tooltip>
          </div>
        </Otherwise>
      </Choose>
    </li>
  );
};

VendorRow.defaultProps = {
  isEditing: false,
  editValue: '',
  hasEditError: false,
};

VendorRow.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  isEditing: PropTypes.bool,
  editValue: PropTypes.string,
  hasEditError: PropTypes.bool,
  onStartRename: PropTypes.func.isRequired,
  onChangeEditValue: PropTypes.func.isRequired,
  onCommitRename: PropTypes.func.isRequired,
  onCancelRename: PropTypes.func.isRequired,
};

export default VendorRow;
